import React, { useEffect } from "react";

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface FaqItem {
  q: string;
  a: string;
}

interface SeoHeadProps {
  title: string;
  description?: string;
  canonical?: string;
  image?: string;
  type?: "website" | "article";
  publishedAt?: string;
  updatedAt?: string;
  readTime?: number;
  breadcrumbs?: BreadcrumbItem[];
  faqItems?: FaqItem[];
  noindex?: boolean;
  lang?: "ru" | "en";
}

const SITE_NAME = "Trend";
const SITE_URL = "https://trend.nasonov.fun";
const DEFAULT_IMAGE = `${SITE_URL}/og-default.svg`;

// В production-билде react-snap пре-рендерит на localhost — noindex туда не пишем.
// В dev-сборке localhost закрываем как обычно.
const isNoindexHost = process.env.NODE_ENV === "production"
  ? window.location.hostname.includes("dev.trend.nasonov.fun")
  : ["dev.trend.nasonov.fun", "localhost"].some(d => window.location.hostname.includes(d));

function setMeta(selector: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    // parse selector like meta[name="foo"] or meta[property="bar"]
    const match = selector.match(/\[(\w+)="([^"]+)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(attrs: Record<string, string>) {
  const hreflang = attrs.hreflang;
  const rel = attrs.rel;
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
}

function setJsonLd(key: string, data: object) {
  let el = document.head.querySelector<HTMLScriptElement>(`script[type="application/ld+json"][data-seo="${key}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo", key);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(key: string) {
  document.head.querySelector(`script[type="application/ld+json"][data-seo="${key}"]`)?.remove();
}

export const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  publishedAt,
  updatedAt,
  readTime,
  breadcrumbs,
  faqItems,
  noindex = false,
  lang = "ru",
}) => {
  const fullTitle = title === SITE_NAME ? title : `${title} — ${SITE_NAME}`;
  const url = canonical ? `${SITE_URL}${canonical}` : SITE_URL;

  useEffect(() => {
    document.title = fullTitle;
    document.documentElement.lang = lang;

    if (description) setMeta('meta[name="description"]', description);
    setMeta('meta[name="robots"]', (noindex || isNoindexHost) ? "noindex,nofollow" : "index,follow");

    setLink({ rel: "canonical", href: url });
    setLink({ rel: "alternate", hreflang: "ru", href: url });
    setLink({ rel: "alternate", hreflang: "en", href: url });
    setLink({ rel: "alternate", hreflang: "x-default", href: url });

    setMeta('meta[property="og:site_name"]', SITE_NAME);
    setMeta('meta[property="og:title"]', fullTitle);
    if (description) setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:type"]', type);
    setMeta('meta[property="og:url"]', url);
    setMeta('meta[property="og:image"]', image);
    setMeta('meta[property="og:locale"]', lang === "ru" ? "ru_RU" : "en_US");
    if (publishedAt) setMeta('meta[property="article:published_time"]', publishedAt);
    if (updatedAt) setMeta('meta[property="article:modified_time"]', updatedAt);

    setMeta('meta[name="twitter:card"]', "summary_large_image");
    setMeta('meta[name="twitter:title"]', fullTitle);
    if (description) setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="twitter:image"]', image);
    if (readTime) {
      setMeta('meta[name="twitter:label1"]', "Время чтения");
      setMeta('meta[name="twitter:data1"]', `${readTime} мин`);
    }

    setJsonLd(
      "main",
      type === "article"
        ? {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: title,
            description,
            image,
            datePublished: publishedAt,
            dateModified: updatedAt ?? publishedAt,
            publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
          }
        : {
            "@context": "https://schema.org",
            "@graph": [
              { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
              { "@type": "Organization", name: SITE_NAME, url: SITE_URL, logo: `${SITE_URL}/og-default.svg` },
            ],
          }
    );

    if (breadcrumbs && breadcrumbs.length > 0) {
      setJsonLd("breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          item: item.url,
        })),
      });
    } else {
      removeJsonLd("breadcrumb");
    }

    if (faqItems && faqItems.length > 0) {
      setJsonLd("faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map(({ q, a }) => ({
          "@type": "Question",
          name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      });
    } else {
      removeJsonLd("faq");
    }
  }, [title, fullTitle, description, url, image, type, publishedAt, updatedAt, readTime, breadcrumbs, faqItems, noindex, lang]);

  return null;
};
